import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../Config';

const VehiculeForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        numero: '',
        marque: '',
        modele: '',
        km_actuel: '',
        user_id: ''
    });

    const [loading, setLoading] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [users, setUsers] = useState([]);

    const token = localStorage.getItem('token');

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (!userData) {
            navigate('/login');
            return;
        }

        const parsedUser = JSON.parse(userData);
        setCurrentUser(parsedUser);

        if (!['admin', 'validateur', 'chef_equipe'].includes(parsedUser.role)) {
            navigate('/vehicules');
            return;
        }

        fetchUsers();

        if (isEditMode) {
            fetchVehicule();
        }
    }, [id, navigate]);

    const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
            const response = await axios.get(`${API_URL}/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            let usersList = [];
            if (Array.isArray(response.data)) {
                usersList = response.data;
            } else if (response.data?.data && Array.isArray(response.data.data)) {
                usersList = response.data.data;
            } else if (response.data?.users && Array.isArray(response.data.users)) {
                usersList = response.data.users;
            }

            setUsers(usersList);
        } catch (err) {
            console.error('Erreur chargement utilisateurs:', err);
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchVehicule = async () => {
        try {
            const response = await axios.get(`${API_URL}/vehicules/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const v = response.data;
            setFormData({
                numero: v.numero || '',
                marque: v.marque || '',
                modele: v.modele || '',
                km_actuel: v.km_actuel ? v.km_actuel.toString() : '',
                user_id: v.user_id || ''
            });
        } catch (err) {
            setError('Erreur lors du chargement du véhicule');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'km_actuel' ? (value === '' ? '' : value.replace(/[^0-9]/g, '')) : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        if (!formData.numero.trim()) {
            setError('Le numéro d\'immatriculation est obligatoire');
            setLoading(false);
            return;
        }

        if (formData.km_actuel && parseInt(formData.km_actuel) < 0) {
            setError('Le kilométrage ne peut pas être négatif');
            setLoading(false);
            return;
        }

        try {
            const dataToSend = {
                ...formData,
                km_actuel: formData.km_actuel ? parseInt(formData.km_actuel) : 0
            };

            if (isEditMode) {
                await axios.put(`${API_URL}/vehicules/${id}`, dataToSend, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccess('Véhicule mis à jour avec succès');
            } else {
                const response = await axios.post(`${API_URL}/vehicules`, dataToSend, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccess('Véhicule créé avec succès');
                setTimeout(() => navigate(`/vehicules/${response.data.id}`), 1500);
            }
            
            setTimeout(() => {
                if (isEditMode) navigate(`/vehicules/${id}`);
            }, 1500);
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || 'Erreur lors de l\'enregistrement';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    if (!currentUser) {
        return (
            <div className="flex items-center justify-center min-h-screen ">
                <div className="text-gray-600">Chargement...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen ">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-2">
                        <h1 className="text-2xl font-bold text-gray-900">
                            {isEditMode ? 'Modifier le véhicule' : 'Nouveau véhicule'}
                        </h1>
                    </div>
                    <p className="text-gray-600">
                        {isEditMode 
                            ? 'Modifiez les informations du véhicule' 
                            : 'Ajoutez un nouveau véhicule à la flotte'
                        }
                    </p>
                </div>

                {/* Messages d'état */}
                <div className="space-y-3 mb-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-center text-red-700">
                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <span>{error}</span>
                            </div>
                        </div>
                    )}

                    {success && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center text-green-700">
                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span>{success}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Formulaire */}
                <div className="bg-white rounded-lg border shadow-sm">
                    <div className="px-6 py-4 border-b">
                        <h2 className="font-medium text-gray-900">
                            Informations du véhicule
                        </h2>
                    </div>

                    <div className="p-6">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Numéro d'immatriculation */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Numéro d'immatriculation *
                                </label>
                                <input
                                    type="text"
                                    name="numero"
                                    value={formData.numero}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Ex: VHC-001"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Marque */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Marque
                                    </label>
                                    <input
                                        type="text"
                                        name="marque"
                                        value={formData.marque}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Ex: Toyota"
                                    />
                                </div>

                                {/* Modèle */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Modèle
                                    </label>
                                    <input
                                        type="text"
                                        name="modele"
                                        value={formData.modele}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Ex: Corolla"
                                    />
                                </div>

                                {/* Kilométrage */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Kilométrage actuel
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="km_actuel"
                                            value={formData.km_actuel}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Ex: 150000"
                                        />
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                            km
                                        </div>
                                    </div>
                                </div>

                                {/* Utilisateur affecté */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Utilisateur affecté
                                    </label>
                                    <select
                                        name="user_id"
                                        value={formData.user_id}
                                        onChange={handleChange}
                                        disabled={loadingUsers}
                                        className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                                    >
                                        <option value="">Non affecté</option>
                                        {loadingUsers ? (
                                            <option disabled>Chargement...</option>
                                        ) : users.length === 0 ? (
                                            <option disabled>Aucun utilisateur</option>
                                        ) : (
                                            users.map((user) => (
                                                <option key={user.id} value={user.id}>
                                                    {user.nom || user.email} {user.role && `(${user.role})`}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="pt-6 border-t">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div className="text-sm text-gray-500">
                                        <span className="font-medium">*</span> Champs obligatoires
                                    </div>
                                    
                                    <div className="flex space-x-3">
                                        <Link
                                            to="/vehicules"
                                            className="px-4 py-2 text-gray-700 border rounded hover:"
                                        >
                                            Annuler
                                        </Link>
                                        
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                        >
                                            {loading ? (
                                                <>
                                                    <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                    </svg>
                                                    Enregistrement...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isEditMode ? "M5 13l4 4L19 7" : "M12 4v16m8-8H4"} />
                                                    </svg>
                                                    {isEditMode ? 'Mettre à jour' : 'Créer'}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Note */}
                <div className="mt-6 text-center">
                    <p className="text-sm text-gray-500">
                        Pour toute assistance, contactez l'administrateur.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default VehiculeForm;